import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Components
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ClientsPage from './components/ClientsPage';
import ApplicationsPage from './components/ApplicationsPage';
import HistoryPage from './components/HistoryPage';
import IntegrationPage from './components/IntegrationPage';
import StaffPage from './components/StaffPage';
import AffiliatesPage from './components/AffiliatesPage';
import AccessPage from './components/AccessPage';
import ClientCard from './components/ClientCard';
import CalendarPage from './components/CalendarPage';
import NotificationManager from './components/NotificationManager';
import NotificationPanel from './components/NotificationPanel';
import TradingApp from './components/trading/TradingApp';
import LandingApp from './components/LandingApp';
import TradingDemo from './components/TradingDemo';
import TradingPlatform from './components/TradingPlatform'; // [CFD-2026-05]
import TradingManagementPage from './components/TradingManagementPage';
import TradingAccountsPage from './components/TradingAccountsPage';
import TradingAccountDetail from './components/TradingAccountDetail';
import ChartTest from './components/ChartTest';
import StatsPage from './components/StatsPage';
import VoiceAgentPage from './components/VoiceAgentPage';

// Contexts
import { ThemeProvider } from './contexts/ThemeContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { BrandProvider } from './contexts/BrandContext';
import { LanguageProvider } from './contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global axios defaults
axios.defaults.timeout = 30000; // 30 sec timeout

// Axios retry interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    // Retry on network errors or 502/503/504 (max 2 retries)
    if (
      config && !config._retryCount &&
      (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' ||
       [502, 503, 504].includes(error.response?.status))
    ) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= 2) {
        await new Promise(r => setTimeout(r, 1000 * config._retryCount));
        return axios(config);
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = React.createContext();

// Protected Route Component - blocks access for specific roles
const RoleProtectedRoute = ({ children, blockedRoles = [], redirectTo = '/crm/dashboard' }) => {
  const { user } = React.useContext(AuthContext);
  
  if (user && blockedRoles.includes(user.role)) {
    console.warn(`🚫 Access denied for role ${user.role} - redirecting to ${redirectTo}`);
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const isStandaloneApp = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  
  // Debug user state changes
  useEffect(() => {
    console.log('🔄 App.js: User state changed:', user);
  }, [user]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Completely disable React error overlay
    if (typeof window !== 'undefined') {
      // Disable error overlay in development
      window.addEventListener('error', (e) => {
        if (e.message === 'ResizeObserver loop limit exceeded' || 
            e.message === 'Script error.' || 
            e.message === 'Script error' ||
            e.message.includes('ResizeObserver')) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      });

      // Disable unhandled promise rejections overlay
      window.addEventListener('unhandledrejection', (e) => {
        e.preventDefault();
      });
      
      // Try to remove error overlay if it exists
      const removeErrorOverlay = () => {
        const overlay = document.querySelector('iframe[style*="fixed"]');
        if (overlay) {
          overlay.remove();
        }
      };
      
      // Check periodically for error overlay and remove it
      const intervalId = setInterval(removeErrorOverlay, 100);
      
      // Cleanup
      return () => {
        clearInterval(intervalId);
      };
    }
  }, []);

  useEffect(() => {
    // TEMPORARILY DISABLED: Setup axios interceptor for handling auth errors
    // const interceptor = axios.interceptors.response.use(
    //   (response) => response,
    //   (error) => {
    //     if (error.response?.status === 401) {
    //       console.warn('🔐 Received 401 Unauthorized, logging out user');
    //       localStorage.removeItem('token');
    //       delete axios.defaults.headers.common['Authorization'];
    //       setUser(null);
    //     }
    //     return Promise.reject(error);
    //   }
    // );

    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      console.log('🔑 Found token in localStorage, verifying...');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token
      axios.get(`${API}/auth/me`)
        .then(response => {
          console.log('✅ Token verification successful:', response.data);
          setUser(response.data);
        })
        .catch((error) => {
          console.error('❌ Token verification failed:', error.response?.data || error.message);
          console.log('🗑️ Removing invalid token from localStorage');
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    // Cleanup interceptor on unmount (disabled for now)
    return () => {
      // axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (identifier, password) => {
    try {
      // [CFD-2026-05] `identifier` is email OR phone — backend handles both
      const response = await axios.post(`${API}/auth/login`, { identifier, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <LanguageProvider>
    <BrandProvider>
      <ThemeProvider>
        <TimezoneProvider>
          <AuthContext.Provider value={{ user, login, logout, setUser }}>
            <Router>
            <div className="App">
            {/* Notification Manager - invisible component for handling notifications */}
            {user && <NotificationManager currentUser={user} backendUrl={BACKEND_URL} />}
            
            <Routes>
              {/* CRM Protected Routes - ADMIN/STAFF ONLY */}
              <Route 
                path="/crm/*" 
                element={
                  user ? 
                    (user.role === 'client' || isStandaloneApp ? 
                      <Navigate to="/trading" replace /> : 
                      <Layout><Outlet /></Layout>
                    ) : 
                    <Navigate to="/" />
                }
              >
                <Route index element={<Navigate to="dashboard" />} />
                <Route path="dashboard" element={
                  <RoleProtectedRoute blockedRoles={['admin', 'sales_manager', 'retention_manager']} redirectTo="/crm/clients">
                    <Dashboard />
                  </RoleProtectedRoute>
                } />
                <Route path="stats" element={<StatsPage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/:id" element={<ClientCard />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route 
                  path="applications" 
                  element={
                    <RoleProtectedRoute blockedRoles={['sales_manager', 'team_lead_sales']}>
                      <ApplicationsPage />
                    </RoleProtectedRoute>
                  } 
                />
                <Route 
                  path="history" 
                  element={
                    <RoleProtectedRoute blockedRoles={['sales_manager', 'team_lead_sales']}>
                      <HistoryPage />
                    </RoleProtectedRoute>
                  } 
                />
                <Route path="integration" element={<IntegrationPage />} />
                <Route path="staff" element={
                  <RoleProtectedRoute blockedRoles={['admin', 'sales_manager', 'team_lead_sales', 'retention_manager', 'team_lead_retention']} redirectTo="/crm/clients">
                    <StaffPage />
                  </RoleProtectedRoute>
                } />
                <Route path="affiliates" element={<AffiliatesPage />} />
                <Route path="access" element={<AccessPage />} />
                <Route path="trading-management" element={<TradingManagementPage />} />
                <Route path="trading-accounts" element={<TradingAccountsPage />} />
                <Route path="trading-accounts/:accountId" element={<TradingAccountDetail />} />
                <Route path="voice-agent" element={
                  <RoleProtectedRoute blockedRoles={['admin', 'sales_manager', 'team_lead_sales', 'retention_manager', 'team_lead_retention', 'manager', 'client']} redirectTo="/crm/dashboard">
                    <VoiceAgentPage />
                  </RoleProtectedRoute>
                } />
              </Route>
              
              
              <Route 
                path="/trading" 
                element={user ? <TradingPlatform /> : <Navigate to="/" />} 
              />
              {/* [CFD-2026-05] /trading/legacy keeps the old TradingDemo for instant rollback */}
              <Route 
                path="/trading/legacy" 
                element={user ? <TradingDemo /> : <Navigate to="/" />} 
              />
              
              <Route 
                path="/login" 
                element={!user ? <Login /> : 
                  user.role === 'client' ? <Navigate to="/trading" /> : <Navigate to="/crm/dashboard" />
                } 
              />
              
              <Route path="/chart-test" element={<ChartTest />} />
              
              {/* Demo Trading Platform - Public Access */}
              <Route path="/demo" element={<TradingPlatform />} />
              <Route path="/demo/legacy" element={<TradingDemo />} />
              
              {/* Landing Site - only for root */}
              <Route path="*" element={<LandingApp />} />
            </Routes>
            
            {/* [CFD-2026-05] Hide global notification bell for clients — CRM staff only */}
            {user && user.role && user.role !== 'client' && <NotificationPanel />}
          </div>
        </Router>
      </AuthContext.Provider>
    </TimezoneProvider>
  </ThemeProvider>
  </BrandProvider>
    </LanguageProvider>
  );
}

export { AuthContext };
export default App;